import React from 'react';
import {
    Button,
    Divider,
    EyeClosedIcon,
    EyeEmptyIcon,
    FocusIcon,
    Menu,
    MenuHandler,
    MenuItem,
    MinusCircledIcon,
    PathIcon,
    PlusCircledIcon,
    RedoIcon,
    UndoIcon
} from '@genestack/ui';
import style from './control-panel.module.css';
import {useHiddenNodesActions} from '../graph-logic/hidden-nodes-actions/use-hidden-nodes-actions';
import {useUndoRedo} from '../../../application/query-workflow/query-layout/completed-query/results-graph/use-undo-redo';
import {getClientOs} from '../../../utils/get-client-os';
import styles from '../../layout/vertical-bar/vertical-bar.module.css';
import {TooltipOptions, wrapWithTooltip} from '../../tooltip/wrap-with-tooltip';

interface Props {
    focusGraph: () => void;
    hiddenNodesActions?: ReturnType<typeof useHiddenNodesActions>;
    undoRedoProps?: ReturnType<typeof useUndoRedo>;
}

function isUndoKeyboardEvent(e: KeyboardEvent) {
    const clientOS = getClientOs();
    if (clientOS === 'MacOS') {
        return e.metaKey && e.key === 'z';
    }

    return e.ctrlKey && e.key === 'z';
}

function getUndoShortcutString() {
    const clientOS = getClientOs();
    return `${clientOS === 'MacOS' ? '⌘' : 'Ctrl+'}Z`;
}

function isRedoKeyboardEvent(e: KeyboardEvent) {
    const clientOS = getClientOs();

    if (clientOS === 'MacOS') {
        return e.metaKey && e.shiftKey && e.key === 'z';
    }

    return e.ctrlKey && e.key === 'y';
}

function getRedoShortcutString() {
    const clientOS = getClientOs();
    if (clientOS === 'MacOS') {
        return '⌘⇧Z';
    }

    return 'Ctrl+Y';
}

const tooltipOptions: TooltipOptions = {
    tooltipClassname: styles.tooltip,
    placement: 'right'
};

export function ControlPanel(props: Props) {
    const undoRedoRef = React.useRef(props.undoRedoProps);
    undoRedoRef.current = props.undoRedoProps;

    React.useEffect(() => {
        if (props.undoRedoProps) {
            const keyDownHandler = (e: KeyboardEvent) => {
                if (isRedoKeyboardEvent(e)) {
                    undoRedoRef.current!.redo();
                    return;
                }

                if (isUndoKeyboardEvent(e)) {
                    undoRedoRef.current!.undo();
                }
            };

            window.addEventListener('keydown', keyDownHandler);

            return () => {
                window.removeEventListener('keydown', keyDownHandler);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const buildPathMenu = !props.hiddenNodesActions ? null : (
        <Menu
            onValueSelect={(val) => {
                if (val === 'connectNodes') {
                    props.hiddenNodesActions!.buildPathProps.connectSelectedNodes();
                }

                if (val === 'pathsThroughNodes') {
                    props.hiddenNodesActions!.buildPathProps.buildPathsThroughSelected();
                }
            }}
        >
            <MenuItem
                key="pathsThroughNodes"
                value="pathsThroughNodes"
                disabled={props.hiddenNodesActions.nonHiddenSelectedNodes.length < 1}
            >
                Keep paths through selected nodes
            </MenuItem>
            <MenuItem
                key="connectNodes"
                value="connectNodes"
                disabled={props.hiddenNodesActions.nonHiddenSelectedNodes.length < 2}
            >
                Keep paths between selected nodes
            </MenuItem>
        </Menu>
    );

    const hideNodesMenu = !props.hiddenNodesActions ? null : (
        <Menu
            onValueSelect={(val) => {
                if (val === 'hideOnlyNodes') {
                    props.hiddenNodesActions!.hideNodesProps.hideSelectedNodes();
                }

                if (val === 'hideNodesAndDeadEnds') {
                    props.hiddenNodesActions!.hideNodesProps.hideNodesAndDeadEnds();
                }

                if (val === 'hideAllNodes') {
                    props.hiddenNodesActions!.hideNodesProps.hideAllNodes();
                }
            }}
        >
            <MenuItem
                key="hideNodesAndPaths"
                value="hideNodesAndDeadEnds"
                disabled={!props.hiddenNodesActions.nonHiddenSelectedNodes.length}
            >
                Hide selected and dead-end nodes
            </MenuItem>
            <MenuItem
                key="hideOnlyNodes"
                value="hideOnlyNodes"
                disabled={!props.hiddenNodesActions.nonHiddenSelectedNodes.length}
            >
                Hide selected nodes only
            </MenuItem>
            <MenuItem key="hideAllNodes" value="hideAllNodes">
                Hide all nodes
            </MenuItem>
        </Menu>
    );

    const unhideNodesMenu = !props.hiddenNodesActions ? null : (
        <Menu
            onValueSelect={(val) => {
                if (val === 'unhideSelectedNodes') {
                    props.hiddenNodesActions!.unhideNodesProps.unhideSelectedNodes();
                }

                if (val === 'unhideAllNodes') {
                    props.hiddenNodesActions!.unhideNodesProps.unhideAllNodes();
                }

                if (val === 'addPathsThroughSelected') {
                    props.hiddenNodesActions!.unhideNodesProps.unhideAllPathsThroughSelected();
                }

                if (val === 'attachToSubgraph') {
                    props.hiddenNodesActions!.unhideNodesProps.connectSelectedToUnhidden();
                }
            }}
        >
            <MenuItem
                key="unhideSelectedNodes"
                value="unhideSelectedNodes"
                disabled={!props.hiddenNodesActions.hiddenSelectedNodes.length}
            >
                Return selected nodes only
            </MenuItem>
            <MenuItem key="unhideAllNodes" value="unhideAllNodes">
                Return all nodes
            </MenuItem>
            <MenuItem
                key="addPathsThroughSelected"
                value="addPathsThroughSelected"
                disabled={!props.hiddenNodesActions.hiddenSelectedNodes.length}
            >
                Return paths going through selected nodes
            </MenuItem>
            <MenuItem
                key="attachToSubgraph"
                value="attachToSubgraph"
                disabled={!props.hiddenNodesActions.hiddenSelectedNodes.length}
            >
                Attach to the current subgraph
            </MenuItem>
        </Menu>
    );

    const onEyeClick = React.useCallback(
        () => props.hiddenNodesActions?.toggleIsHiddenNodesVisible(),
        [props.hiddenNodesActions]
    );

    return (
        <div className={style.controlPanel}>
            {wrapWithTooltip(
                <Button ghost size="small" iconStart={<FocusIcon />} onClick={props.focusGraph} />,
                'Centre and fit',
                tooltipOptions
            )}

            {props.hiddenNodesActions ? (
                <React.Fragment>
                    <Divider />
                    {wrapWithTooltip(
                        <MenuHandler menu={buildPathMenu!}>
                            <Button
                                ghost
                                size="small"
                                iconStart={<PathIcon />}
                                hasSubmenuIndicator
                                disabled={!props.hiddenNodesActions.nonHiddenSelectedNodes.length}
                            />
                        </MenuHandler>,
                        props.hiddenNodesActions.nonHiddenSelectedNodes.length
                            ? 'Find paths'
                            : 'To find paths, select at least one node',
                        tooltipOptions
                    )}

                    {wrapWithTooltip(
                        <MenuHandler menu={hideNodesMenu!}>
                            <Button
                                ghost
                                size="small"
                                iconStart={<MinusCircledIcon />}
                                hasSubmenuIndicator
                                disabled={props.hiddenNodesActions.isAllNodesHidden}
                            />
                        </MenuHandler>,
                        props.hiddenNodesActions.isAllNodesHidden
                            ? 'Hide nodes (all nodes are hidden)'
                            : 'Hide nodes',
                        tooltipOptions
                    )}

                    {wrapWithTooltip(
                        <MenuHandler menu={unhideNodesMenu!}>
                            <Button
                                ghost
                                size="small"
                                iconStart={<PlusCircledIcon />}
                                hasSubmenuIndicator
                                disabled={!props.hiddenNodesActions.hasHiddenNodes}
                            />
                        </MenuHandler>,
                        props.hiddenNodesActions.hasHiddenNodes
                            ? 'Return hidden nodes'
                            : 'Return hidden nodes (no hidden nodes)',
                        tooltipOptions
                    )}

                    <Divider />
                    {wrapWithTooltip(
                        <Button
                            ghost
                            size="small"
                            iconStart={
                                props.hiddenNodesActions.isHiddenNodesVisible ? (
                                    <EyeEmptyIcon />
                                ) : (
                                    <EyeClosedIcon />
                                )
                            }
                            active={props.hiddenNodesActions.isHiddenNodesVisible}
                            disabled={!props.hiddenNodesActions.hasHiddenNodes}
                            onClick={onEyeClick}
                        />,
                        'Hidden nodes visibility toggle',
                        tooltipOptions
                    )}

                    {props.undoRedoProps && (
                        <React.Fragment>
                            <Divider />
                            {wrapWithTooltip(
                                <Button
                                    ghost
                                    size="small"
                                    iconStart={<UndoIcon />}
                                    disabled={!props.undoRedoProps.canUndo}
                                    onClick={props.undoRedoProps.undo}
                                />,
                                `Undo (${getUndoShortcutString()})`,
                                tooltipOptions
                            )}
                            {wrapWithTooltip(
                                <Button
                                    ghost
                                    size="small"
                                    iconStart={<RedoIcon />}
                                    disabled={!props.undoRedoProps.canRedo}
                                    onClick={props.undoRedoProps.redo}
                                />,
                                `Redo (${getRedoShortcutString()})`,
                                tooltipOptions
                            )}
                        </React.Fragment>
                    )}
                </React.Fragment>
            ) : null}
        </div>
    );
}
